//
// actions
//
import * as http from '@/libs/http'
import * as time from '@/libs/time'
import * as calendar from '@/app/special/libs/calendar'
import * as funcs from './funcs'

export default {
  // 新規予約用に初期化する
  async newBook ({ rootGetters, commit, dispatch }, init) {
    const shopid = rootGetters['shop/shopID']

    // オプションのIDごとの数量を保持する Object を作成する
    const options = rootGetters['shop/m_options'].reduce((v, item) => {
      v[item.id] = 0
      return v
    }, {})

    // レンタルギアのIDごとの数量を保持する Object を作成する
    const gears = rootGetters['shop/m_gears'].reduce((v, item) => {
      v[item.id] = 0
      return v
    }, {})

    // 初期化データを作成する
    const book = {
      shopid,
      allow: null,
      period: null,
      carid: 0,
      options,
      gears,
      insuranceid: -1,
      locationid: 0
    }

    if (init.allow) {
      book.allow = init.allow
    }

    if (init.period) {
      const data = await http.request(async api => {
        const resp = await api.get(`shops/${shopid}/special-optday`, {
          params: {
            carid: init.carid,
            s: init.period.begin,
            e: init.period.end
          }
        })
        if (resp.status === http.OK) {
          return resp.data
        }
        return new http.StatusError(resp)
      })
      if (data === http.FAILED) {
        return
      }
      book.period = {
        ...init.period,
        optdays: data.optdays,
        insurancedays: data.insurancedays,
        insuranceTerm: data.insuranceTerm,
        hiseason: data.hiseason
      }
    }

    if (init.carid) {
      book.carid = init.carid
    }

    commit('initBook', book)

    // 料金を計算する
    await Promise.all([
      dispatch('calcChargeBasic'),
      dispatch('calcChargeAdditional')
    ])
  },

  // 予約可能期間を設定する
  async setBookAllow ({ commit }, allow) {
    commit('setBookAllow', allow)
  },

  // 期間を設定する
  async setBookPeriod ({ state, commit, dispatch }, period) {
    // オプションの適用日数を取得する
    const data = await http.request(async api => {
      const resp = await api.get(`shops/${state.shopid}/special-optday`, {
        params: {
          carid: state.carid,
          s: period.begin,
          e: period.end
        }
      })
      if (resp.status === http.OK) {
        return resp.data
      }
      return new http.StatusError(resp)
    })
    if (data === http.FAILED) {
      return false
    }

    // 期間を設定する
    commit('setBookPeriod', {
      ...period,
      optdays: data.optdays,
      insurancedays: data.insurancedays,
      insuranceTerm: data.insuranceTerm,
      hiseason: data.hiseason
    })

    // 料金を計算する
    await Promise.all([
      dispatch('calcChargeBasic'),
      dispatch('calcChargeAdditional')
    ])

    // ハイシーズンを含む期間の場合、24Hより多い制約を設ける
    if (data.hiseason) {
      const hours = (period.end - period.begin) / time.HOUR
      if (hours <= 24) {
        return 'constraint:hiseason24'
      }
    }

    return 'ok'
  },

  // 車両IDを設定する
  async setBookCarID ({ commit, dispatch }, carid) {
    // 車両IDを設定する
    commit('setBookCarID', carid)

    // 基本料金を計算する
    await dispatch('calcChargeBasic')

    // 車両装備のため、追加料金を計算する
    await dispatch('calcChargeAdditional')
  },

  // オプションを個別に設定する
  async setBookOption ({ commit, dispatch }, opt) {
    // オプション情報を設定する
    commit('setBookOption', opt)

    // 追加料金を計算する
    await dispatch('calcChargeAdditional')
  },

  // レンタルギアを個別に設定する
  async setBookGear ({ commit, dispatch }, opt) {
    // レンタルギア情報を設定する
    commit('setBookGear', opt)

    // 追加料金を計算する
    await dispatch('calcChargeAdditional')
  },

  // 返却場所を設定する
  async setBookLocationID ({ commit, dispatch }, locationid) {
    // 返却場所を設定する
    commit('setLocationID', locationid)

    // 追加料金を計算する
    await dispatch('calcChargeAdditional')
  },

  // 送迎を設定する
  async setBookPick ({ commit, dispatch }, pick) {
    // 送迎情報を設定する
    commit('setBookPick', pick)

    // 追加料金を計算する
    await dispatch('calcChargeAdditional')
  },

  // 配車を設定する
  async setBookDeli ({ commit, dispatch }, deli) {
    // 配車情報を設定する
    commit('setBookDeli', deli)

    // 追加料金を計算する
    await dispatch('calcChargeAdditional')
  },

  // 免責補償を設定する
  async setBookInsuranceID ({ commit, dispatch }, insuranceid) {
    // 免責情報を設定する
    commit('setBookInsuranceID', insuranceid)

    // 追加料金を計算する
    await dispatch('calcChargeAdditional')
  },

  // クーポンコードを設定する
  async setBookCouponCode ({ state, getters, rootGetters, commit, dispatch }, couponCode) {
    const result = {
      ok: false,
      reason: '',
      coupon: null
    }

    do {
      if (!couponCode) {
        break
      }

      // 対応するクーポンを取得する
      const coupons = rootGetters['shop/m_coupons'] || []
      const coupon = coupons.find(item => item.condvalue === couponCode)
      if (!coupon) {
        result.reason = 'クーポンコードが正しくありません。'
        break
      }

      // 適用条件判定: ワンショット割引の未使用チェック
      if (coupon.condType === 2) {
        if (coupon.rid && coupon.rid !== state.rid) {
          result.reason = 'このクーポンは使用済みです。'
          break
        }
      }

      // 適用条件判定: クラス名
      if (coupon.className) {
        const car = getters.bookCar
        if (!car || car.classname !== coupon.className) {
          result.reason = `このクーポンは車両が${coupon.className}クラスでのみ利用可能です。`
          break
        }
      }

      // 適用条件判定: 期間
      if (coupon.datetype === 0) {
        // 期間条件なし
        break
      }

      const beginTime = time.make(coupon.begintime)
      const endTime = time.make(coupon.endtime)
      if (!beginTime || !endTime) {
        result.reason = 'このクーポンは利用できません。'
        break
      }
      const first = beginTime.valueOf()
      const last = endTime.valueOf()

      switch (coupon.datetype) {
        case 1:
          // 予約日時基準 (予約日時が有効期限内であること)
          {
            const now = Date.now()
            if (now < first || last < now) {
              result.reason = '利用期間が' + time.format('Y年m月d日', beginTime) + 'から' + time.format('Y年m月d日', endTime) + 'までのため、利用できません。'
            }
          }
          break
        case 2:
          // 利用日時基準 (出発/帰着が有効期限内であること)
          {
            const period = getters.bookPeriod
            const begin = period.begin.valueOf()
            const end = period.end.valueOf()
            if (begin < first || last < end) {
              result.reason = '利用できる予約日が' + time.format('Y年m月d日', beginTime) + 'から' + time.format('Y年m月d日', endTime) + 'までのため、利用できません。'
            }
          }
          break
      }
    } while (false)

    if (result.reason) {
      couponCode = ''
    }

    // クーポンコードを設定する
    commit('setBookCouponCode', couponCode)

    // 割引料金を計算する
    await dispatch('calcChargeDiscount')

    if (!result.reason) {
      result.ok = true
      if (getters.bookCoupons.length > 0) {
        result.coupon = getters.bookCoupons[0]
      }
    }

    return result
  },

  // 連絡事項を設定する
  setBookNote ({ commit }, note) {
    commit('setBookNote', note)
  },

  //
  // 内部アクション
  //
  async calcChargeBasic ({ state, commit, dispatch }) {
    // 因子情報を取得する
    const begin = state.period.begin
    const end = state.period.end
    const carid = state.carid

    // 算出条件が揃っていなければ、0 円
    if (!begin || !end || !carid) {
      commit('setBookChargeBase', 0)
      return
    }

    // 基本料金を得る
    const price = await http.request(async api => {
      const resp = await api.get(`shops/${state.shopid}/special-cbasic`, {
        params: {
          carid: carid,
          s: begin,
          e: end
        }
      })
      if (resp.status === http.OK) {
        return resp.data.basic
      }
    })
    if (price === http.FAILED) {
      return
    }

    // 基本料金を設定する
    commit('setBookChargeBase', price)

    // 割引料金を計算する
    dispatch('calcChargeDiscount')
  },

  calcChargeAdditional ({ state, getters, rootGetters, commit, dispatch }) {
    // 因子情報を取得する
    const optdays = state.period.optdays
    const insurancedays = state.period.insurancedays
    const carid = state.carid

    // 算出条件が揃っていなければ、0 円
    if (!optdays || !insurancedays || !carid) {
      commit('setBookChargeAdditional', 0)
      return
    }

    let price = 0

    // オプション料金を加算する
    price += getters.bookOptions.reduce((price, opt) => {
      return price + opt.charge
    }, 0)

    // 返却場所の料金を加算する
    const loc = rootGetters['shop/m_locations'].find(item => item.id === state.locationid)
    if (loc) {
      price += loc.price
    }

    // 送迎・配車の料金を加算する
    price += funcs.deliPrice(rootGetters['shop/m_pick'], state.pick)
    price += funcs.deliPrice(rootGetters['shop/m_deli'], state.deli)

    // 免責料金を加算する
    const ins = rootGetters['shop/m_insurances'].find(item => item.id === state.insuranceid)
    if (ins) {
      price += funcs.insurancePrice(ins, insurancedays)
    }

    // 追加料金を設定する
    commit('setBookChargeAdditional', price)

    // 割引料金を計算する
    dispatch('calcChargeDiscount')
  },

  calcChargeDiscount ({ getters, commit }) {
    const coupons = getters.bookCoupons

    if (!coupons) {
      commit('setBookChargeDiscount', 0)
      return
    }

    const price = coupons.reduce((price, cpn) => {
      return price + cpn.charge
    }, 0)

    // 割引料金を設定する
    commit('setBookChargeDiscount', price)
  },

  async registerBook ({ state, getters, rootGetters, commit }) {
    // TODO: 予約を登録する
    if (state.rid > 0) {
      return {
        ok: true,
        reasons: [
          'この予約内容はすでに予約されています。',
          'ご来店を心よりお待ちしております。'
        ]
      }
    }

    const cal = calendar.create(rootGetters['shop/m_calendar'], 'daysand', 'book')
    const alloc = cal.addInterval(state.period, rootGetters['shop/profile'].intervalHour)

    const result = await http.request(async api => {
      const resp = await api.post(`shops/${state.shopid}/reserve`, {
        reserve: {
          userID: rootGetters['login/userID'],
          period: {
            begin: state.period.begin,
            end: state.period.end,
            allocbegin: time.format('Y-m-d H:i', alloc.begin),
            allocend: time.format('Y-m-d H:i', alloc.end)
          },
          car: getters.bookCar,
          options: getters.bookOptions,
          gears: getters.bookGears,
          insurance: getters.bookInsurance,
          location: getters.bookLocation,
          pick: getters.bookPick,
          deli: getters.bookDeli,
          peoples: state.peoples,
          couponCode: state.couponCode,
          coupons: getters.bookCoupons,
          note: state.note,
          paymethod: state.paymethod,
          paytoken: state.paytoken,
          charge: getters.bookCharge
        }
      })
      if (resp.status === http.Created || resp.status === http.OK || resp.status === http.Conflict) {
        return resp.data
      }
      return new http.StatusError(resp)
    })

    // 結果を返す
    if (result === http.FAILED) {
      return {
        ok: false,
        reasons: []
      }
    }
    if (result.ok) {
      // 予約IDを上書きする
      commit('setReserveID', result.rid)
    }
    return result
  }
}
